import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, unref as _unref, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "project-planning" }
const _hoisted_2 = {
  key: 0,
  class: "planning-loading"
}
const _hoisted_3 = {
  key: 1,
  class: "planning-error"
}
const _hoisted_4 = {
  key: 2,
  class: "planning-empty"
}
const _hoisted_5 = {
  key: 3,
  class: "planning-tabs"
}
const _hoisted_6 = {
  class: "tab-bar",
  role: "tablist"
}
const _hoisted_7 = ["aria-selected"]
const _hoisted_8 = ["aria-selected", "title", "onClick"]
const _hoisted_9 = { class: "tab-btn__label" }
const _hoisted_10 = {
  key: 1,
  role: "tab",
  class: "tab-btn tab-btn--active",
  "aria-selected": "true"
}
const _hoisted_11 = {
  key: 0,
  class: "tab-panel",
  role: "tabpanel"
}
const _hoisted_12 = {
  key: 0,
  class: "planning-empty"
}
const _hoisted_13 = {
  key: 1,
  class: "gantt-wrap"
}
const _hoisted_14 = {
  key: 0,
  class: "tab-panel",
  role: "tabpanel"
}
const _hoisted_15 = {
  key: 0,
  class: "planning-empty"
}
const _hoisted_16 = {
  key: 1,
  class: "gantt-wrap"
}
const _hoisted_17 = {
  key: 1,
  class: "tab-panel",
  role: "tabpanel"
}
const _hoisted_18 = {
  key: 0,
  class: "planning-empty"
}
const _hoisted_19 = {
  key: 1,
  class: "gantt-wrap"
}

import { ref, computed, onMounted } from 'vue';
import { GGanttChart, GGanttRow } from '@infectoone/vue-ganttastic';
import projectPhasesService from '@/services/projectPhases.service';
import projectTasksService from '@/services/projectTasks.service';

interface PhaseItem {
  id: number;
  name: string;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  status?: string | null;
}

interface TaskItem {
  id: number;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  phaseId?: number | null;
  status?: string | null;
}

interface GanttBar {
  myStart: string;
  myEnd: string;
  ganttBarConfig: {
    id: string;
    label: string;
    style: Record<string, string>;
  };
}

interface GanttRow {
  id: string;
  label: string;
  bars: GanttBar[];
}

interface GanttData {
  rows: GanttRow[];
  chartStart: string;
  chartEnd: string;
}

interface Props {
  projectId: number | string;
  project?: any;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectPlanning',
  props: {
    projectId: {},
    project: {}
  },
  setup(__props: any) {

const props = __props;

const loading = ref(true);
const error = ref<string | null>(null);
const phases = ref<PhaseItem[]>([]);
const tasks = ref<TaskItem[]>([]);
const activeTab = ref<string>('phases');

// ── helpers ───────────────────────────────────────────────────────────────────

function formatMonthLabel(date: Date): string {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${mm}/${yy}`;
}

function statusClass(status?: string | null): string {
  if (!status) return 'not_started';
  return status.toLowerCase();
}

function statusColor(status?: string | null): string {
  switch (status?.toUpperCase()) {
    case 'COMPLETED': return '#4CAF50';
    case 'IN_PROGRESS': return '#2196F3';
    case 'ON_HOLD':     return '#FF9800';
    case 'CANCELLED':   return '#9E9E9E';
    default:            return '#90CAF9';
  }
}

function toGanttDate(dateStr: string | null | undefined, endOfDay = false): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${endOfDay ? '23:59' : '00:00'}`;
}

function fmt(d: Date, time: string): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${time}`;
}

function buildChartBounds(rows: GanttRow[]): { chartStart: string; chartEnd: string } {
  const allDates: Date[] = [];
  for (const row of rows) {
    for (const bar of row.bars) {
      const s = new Date(bar.myStart.replace(' ', 'T'));
      const e = new Date(bar.myEnd.replace(' ', 'T'));
      if (!isNaN(s.getTime())) allDates.push(s);
      if (!isNaN(e.getTime())) allDates.push(e);
    }
  }
  if (allDates.length === 0) {
    const today = new Date();
    return {
      chartStart: fmt(new Date(today.getFullYear(), today.getMonth(), 1), '00:00'),
      chartEnd:   fmt(new Date(today.getFullYear(), today.getMonth() + 2, 1), '00:00'),
    };
  }
  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
  const startBound = new Date(minDate); startBound.setDate(startBound.getDate() - 1);
  const endBound   = new Date(maxDate); endBound.setDate(endBound.getDate() + 1);
  return { chartStart: fmt(startBound, '00:00'), chartEnd: fmt(endBound, '00:00') };
}

function makePhaseRow(phase: PhaseItem): GanttRow {
  const bars: GanttBar[] = [];
  const start = toGanttDate(phase.plannedStartDate, false);
  const end   = toGanttDate(phase.plannedEndDate, true);
  if (start && end) {
    bars.push({ myStart: start, myEnd: end, ganttBarConfig: { id: `phase-${phase.id}`, label: phase.name, style: { background: statusColor(phase.status), borderRadius: '6px', color: '#fff', fontWeight: '600', boxShadow: '0 2px 6px rgba(0,0,0,0.25)' } } });
  }
  return { id: `phase-row-${phase.id}`, label: phase.name, bars };
}

function makeTaskRow(task: TaskItem): GanttRow {
  const bars: GanttBar[] = [];
  const start = toGanttDate(task.startDate, false);
  const end   = toGanttDate(task.endDate, true);
  if (start && end) {
    bars.push({ myStart: start, myEnd: end, ganttBarConfig: { id: `task-${task.id}`, label: task.name, style: { background: statusColor(task.status), borderRadius: '5px', color: '#fff', fontWeight: '500', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' } } });
  }
  return { id: `task-row-${task.id}`, label: task.name, bars };
}

function normaliseArray<T>(result: any): T[] {
  if (Array.isArray(result)) return result as T[];
  if (Array.isArray(result?.data)) return result.data as T[];
  if (Array.isArray(result?.content)) return result.content as T[];
  return [];
}

// ── computed gantt data per tab ───────────────────────────────────────────────

const phaseGantt = computed<GanttData>(() => {
  const rows = phases.value.map(makePhaseRow);
  return { rows, ...buildChartBounds(rows) };
});

const taskGanttMap = computed<Record<number, GanttData>>(() => {
  const map: Record<number, GanttData> = {};
  for (const phase of phases.value) {
    const rows = tasks.value.filter((t: TaskItem) => t.phaseId === phase.id).map(makeTaskRow);
    map[phase.id] = { rows, ...buildChartBounds(rows) };
  }
  return map;
});

const taskOnlyGantt = computed<GanttData>(() => {
  const rows = tasks.value.map(makeTaskRow);
  return { rows, ...buildChartBounds(rows) };
});

// ── data loading ──────────────────────────────────────────────────────────────

onMounted(async () => {
  loading.value = true;
  error.value = null;
  try {
    const projectIdStr = String(props.projectId);
    if (props.project?.multiPhase) {
      const [phasesResult, tasksResult] = await Promise.all([
        projectPhasesService.getPhases(projectIdStr),
        projectTasksService.getTasks(projectIdStr),
      ]);
      phases.value = normaliseArray<PhaseItem>(phasesResult);
      tasks.value  = normaliseArray<TaskItem>(tasksResult);
    } else {
      const tasksResult = await projectTasksService.getTasks(projectIdStr);
      tasks.value  = normaliseArray<TaskItem>(tasksResult);
      phases.value = [];
    }
    activeTab.value = phases.value.length > 0 ? 'phases' : 'tasks';
  } catch (err: any) {
    error.value = err?.message ?? 'Failed to load planning data';
  } finally {
    loading.value = false;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _cache[1] || (_cache[1] = _createElementVNode("div", { class: "loading-spinner" }, null, -1)),
          _createElementVNode("p", null, _toDisplayString(_ctx.$t('common.loading')), 1)
        ]))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("p", null, _toDisplayString(error.value), 1)
          ]))
        : (phases.value.length === 0 && tasks.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("p", null, _toDisplayString(_ctx.$t('projects.planning.noPhases')), 1)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                (phases.value.length > 0)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      role: "tab",
                      class: _normalizeClass(["tab-btn", { 'tab-btn--active': activeTab.value === 'phases' }]),
                      "aria-selected": activeTab.value === 'phases',
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (activeTab.value = 'phases'))
                    }, _toDisplayString(_ctx.$t('projects.planning.gantt.phasesTab')), 11, _hoisted_7))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(phases.value, (phase) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: phase.id,
                    role: "tab",
                    class: _normalizeClass(["tab-btn", { 'tab-btn--active': activeTab.value === `phase-${phase.id}` }]),
                    "aria-selected": activeTab.value === `phase-${phase.id}`,
                    title: phase.name,
                    onClick: ($event: any) => (activeTab.value = `phase-${phase.id}`)
                  }, [
                    _createElementVNode("span", {
                      class: _normalizeClass(["tab-btn__badge", `tab-btn__badge--${statusClass(phase.status)}`])
                    }, null, 2),
                    _createElementVNode("span", _hoisted_9, _toDisplayString(phase.name), 1)
                  ], 10, _hoisted_8))
                }), 128)),
                (phases.value.length === 0)
                  ? (_openBlock(), _createElementBlock("button", _hoisted_10, _toDisplayString(_ctx.$t('projects.planning.gantt.tasksTab')), 1))
                  : _createCommentVNode("", true)
              ]),
              (activeTab.value === 'phases')
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    (phaseGantt.value.rows.length === 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                          _createElementVNode("p", null, _toDisplayString(_ctx.$t('projects.planning.gantt.noData')), 1)
                        ]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                          _createVNode(_unref(GGanttChart), {
                            "chart-start": phaseGantt.value.chartStart,
                            "chart-end": phaseGantt.value.chartEnd,
                            precision: "month",
                            "bar-start": "myStart",
                            "bar-end": "myEnd",
                            "push-on-overlap": false,
                            "label-column-title": _ctx.$t('projects.planning.gantt.phasesTab'),
                            "label-column-width": "280px",
                            "row-height": 52,
                            grid: true
                          }, {
                            timeunit: _withCtx(({ date }) => [
                              _createTextVNode(_toDisplayString(formatMonthLabel(date)), 1)
                            ]),
                            default: _withCtx(() => [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(phaseGantt.value.rows, (row) => {
                                return (_openBlock(), _createBlock(_unref(GGanttRow), {
                                  key: row.id,
                                  label: row.label,
                                  bars: row.bars
                                }, null, 8, ["label", "bars"]))
                              }), 128))
                            ]),
                            _: 1
                          }, 8, ["chart-start", "chart-end", "label-column-title"])
                        ]))
                  ]))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(phases.value, (phase) => {
                return (_openBlock(), _createElementBlock(_Fragment, {
                  key: phase.id
                }, [
                  (activeTab.value === `phase-${phase.id}`)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                        (!taskGanttMap.value[phase.id] || taskGanttMap.value[phase.id].rows.length === 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                              _createElementVNode("p", null, _toDisplayString(_ctx.$t('projects.planning.gantt.noTasksForPhase')), 1)
                            ]))
                          : (_openBlock(), _createElementBlock("div", _hoisted_16, [
                              _createVNode(_unref(GGanttChart), {
                                "chart-start": taskGanttMap.value[phase.id].chartStart,
                                "chart-end": taskGanttMap.value[phase.id].chartEnd,
                                precision: "month",
                                "bar-start": "myStart",
                                "bar-end": "myEnd",
                                "push-on-overlap": false,
                                "label-column-title": _ctx.$t('projects.planning.gantt.taskColumn'),
                                "label-column-width": "280px",
                                "row-height": 48,
                                grid: true
                              }, {
                                timeunit: _withCtx(({ date }) => [
                                  _createTextVNode(_toDisplayString(formatMonthLabel(date)), 1)
                                ]),
                                default: _withCtx(() => [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(taskGanttMap.value[phase.id].rows, (row) => {
                                    return (_openBlock(), _createBlock(_unref(GGanttRow), {
                                      key: row.id,
                                      label: row.label,
                                      bars: row.bars
                                    }, null, 8, ["label", "bars"]))
                                  }), 128))
                                ]),
                                _: 2
                              }, 1032, ["chart-start", "chart-end", "label-column-title"])
                            ]))
                      ]))
                    : _createCommentVNode("", true)
                ], 64))
              }), 128)),
              (phases.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                    (taskOnlyGantt.value.rows.length === 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                          _createElementVNode("p", null, _toDisplayString(_ctx.$t('projects.planning.noPhases')), 1)
                        ]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_19, [
                          _createVNode(_unref(GGanttChart), {
                            "chart-start": taskOnlyGantt.value.chartStart,
                            "chart-end": taskOnlyGantt.value.chartEnd,
                            precision: "month",
                            "bar-start": "myStart",
                            "bar-end": "myEnd",
                            "push-on-overlap": false,
                            "label-column-title": _ctx.$t('projects.planning.gantt.taskColumn'),
                            "label-column-width": "280px",
                            "row-height": 48,
                            grid: true
                          }, {
                            timeunit: _withCtx(({ date }) => [
                              _createTextVNode(_toDisplayString(formatMonthLabel(date)), 1)
                            ]),
                            default: _withCtx(() => [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(taskOnlyGantt.value.rows, (row) => {
                                return (_openBlock(), _createBlock(_unref(GGanttRow), {
                                  key: row.id,
                                  label: row.label,
                                  bars: row.bars
                                }, null, 8, ["label", "bars"]))
                              }), 128))
                            ]),
                            _: 1
                          }, 8, ["chart-start", "chart-end", "label-column-title"])
                        ]))
                  ]))
                : _createCommentVNode("", true)
            ]))
  ]))
}
}

})